import {IconUsers} from "@tabler/icons-react";
import {memo, useCallback, useRef, useState} from "react";
import styles from "./StoryItem.module.css";

/**
 * @param {Object} props
 * @param {import("../stories.constants").Story} props.story
 * @param {(story: import("../stories.constants").Story, anchorElement?: HTMLElement) => void} props.onOpen
 * @param {string} [props.userInitials] - Only for user-type stories
 */
const StoryItem = ({story, onOpen, userInitials = ""}) => {
  const [imgError, setImgError] = useState(false);
  const itemRef = useRef(null);

  const handleClick = useCallback(() => {
    const scrollY = window.scrollY;
    const row = itemRef.current?.closest("[class*='stories__row']");
    const scrollLeft = row?.scrollLeft ?? 0;

    onOpen(story, itemRef.current);

    requestAnimationFrame(() => {
      window.scrollTo({top: scrollY, behavior: "instant"});
      if (row) row.scrollLeft = scrollLeft;
    });
  }, [onOpen, story]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(story, itemRef.current);
      }
    },
    [onOpen, story]
  );

  const ringClass = [
    styles["story-item__ring"],
    story.ringVariant ? styles[`story-item__ring--${story.ringVariant}`] : "",
    story.viewed ? styles["story-item__ring--viewed"] : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderAvatar = () => {
    if (story.type === "community") {
      return (
        <span className={styles["story-item__community-icon"]}>
          <IconUsers size={26} />
        </span>
      );
    }

    if (story.type === "user") {
      if (!imgError && story.icon) {
        return (
          <img
            className={styles["story-item__avatar-img"]}
            src={story.icon}
            alt={story.label}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        );
      }
      return <span className={styles["story-item__initials"]}>{userInitials}</span>;
    }

    if (story.type === "provider" && story.icon) {
      return (
        <img
          className={styles["story-item__avatar-img"]}
          src={story.icon}
          alt={story.label}
          loading="lazy"
        />
      );
    }

    if (story.icon) {
      return (
        <img
          className={styles["story-item__avatar-img"]}
          src={story.icon}
          alt={story.label}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      );
    }

    return null;
  };

  return (
    <div
      ref={itemRef}
      className={styles["story-item"]}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Ver historia: ${story.label}`}
    >
      <div className={ringClass}>
        <div className={styles["story-item__avatar"]}>{renderAvatar()}</div>
      </div>
      <span className={styles["story-item__label"]}>{story.label}</span>
    </div>
  );
};

export default memo(StoryItem);
